import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  getNow,
  getClockOverride,
  setClockOverride,
  isSimulating,
  setSimulating as libSetSimulating,
  subscribe,
} from '../lib/clock'
import { ClockContext, ClockControlsContext } from './ClockContext'

// Provides the overridable clock. When `setOverride` is called we re-key the
// subtree so every component re-reads getNow()/todayLocalISO() against the new
// time — even pages that read "now" directly at render (HomePage, EventBrowse,
// profile/group filtering) rather than through this context.
//
// The remount key is bumped per explicit setOverride call, NOT per override
// value: this lets in-place callers (the schedule's drag-to-retime now-line)
// update the override through the lib directly and have the context value sync
// — via the subscription below — *without* remounting the subtree, so the grid
// keeps its scroll position and selected day mid-drag.
export function ClockProvider({ children }: { children: ReactNode }) {
  const [now, setNow] = useState(() => getNow())
  const [override, setOverrideState] = useState<Date | null>(() => getClockOverride())
  const [simulating, setSimulatingState] = useState(() => isSimulating())
  const [remountNonce, setRemountNonce] = useState(0)

  useEffect(() => {
    const tick = () => {
      setNow(getNow())
      setOverrideState(getClockOverride())
      setSimulatingState(isSimulating())
    }
    // Subscription handles override/simulation changes immediately
    const unsub = subscribe(tick)
    // Periodic interval keeps `now` fresh when running on real time (no
    // override/simulation) — so the HeaderLogo clock hands, the now-line,
    // and other time-dependent UI update as real minutes pass.
    const id = setInterval(tick, 60_000)
    return () => {
      unsub()
      clearInterval(id)
    }
  }, [])

  const setOverride = useCallback((date: Date | null) => {
    setClockOverride(date)
    setNow(getNow())
    setOverrideState(date)
    setRemountNonce(n => n + 1)
  }, [])

  const setSimulating = useCallback((on: boolean) => {
    libSetSimulating(on)
    setNow(getNow())
    setSimulatingState(on)
    setOverrideState(getClockOverride())
  }, [])

  const controls = useMemo(() => ({ setOverride, setSimulating }), [setOverride, setSimulating])

  const key = override === null ? 'live' : `set-${remountNonce}`

  return (
    <ClockContext.Provider value={{ now, override, setOverride, simulating, setSimulating }}>
      <ClockControlsContext.Provider value={controls}>
        <div key={key} className="contents">
          {children}
        </div>
      </ClockControlsContext.Provider>
    </ClockContext.Provider>
  )
}
